import { defineStore } from 'pinia'
import {computed} from "vue";

export const useStageStore = defineStore({
    id: 'stage',
    state: () => ({
        previewMode: false,
        stage: 0,
        stagesData: [],
        isLoading: false,
        language: 'fi_FI',
        currentProgress: 0,
        answerEditUrl: '',
        answersData: [],
        showMap: false,
        totalPoints: 0,
        pointsPerStage: [],
        surveyAnswerUuid: null,
        nameSpace: '',
        answerURL: '',
        answerToken: 0,
        answerId: 0,
        isDraft: null,
        showConfirmSubmitForm: false,
        showDraftForm: false,
        showDraftMessage: false,
        showPasswordForm: false,
        passwordFailed: false,
        pointsPerPathType: [],
        totalPathTypePoints: 0
    }),
    actions: {
        async getSurveyFormData(url) {
            this.isLoading = true;

            try {
                const res = await fetch(url)
                const data = await res.json();
                this.stagesData = data.survey ? data.survey : [];

                if (this.stagesData) {
                    this.addStageNavigationData(this.stagesData.data.stages);
                    this.updateStageAccessStatus(this.stagesData.data.stages);
                    this.managePointsByPathType();
                }

                this.isLoading = false;
            } catch (error) {
                console.log(`Error: ${error}`)
            }
        },
        async sendSurveyFormData(data) {
            this.isLoading = true;

            const dataParams = {
                [ `${this.nameSpace}answerId` ]: this.answerId,
                [ `${this.nameSpace}answerToken` ]: this.answerToken,
                [ `${this.nameSpace}answers` ]: JSON.stringify(this.answersData),
                [ `${this.nameSpace}formDate` ]: Date.now()
            }

            let dataCollection = {
                ...data,
                ...dataParams
            }

            let bodyData = new URLSearchParams(dataCollection).toString();

            const response = await fetch(this.getAnswerURL, {
                method: "POST",
                headers: {
                    // "Content-Type": "application/json",
                    'content-type': 'application/x-www-form-urlencoded'
                },
                body: bodyData
            });

            // Check response status
            if (!response.ok) {
                this.isLoading = false;
                return false
            }

            // Check response result status
            var result = undefined;
            try {
                result = await response.json();
            } catch (error) {
                console.error(`Error: ${error}`)
            }

            if (!result) {
                console.error('Result could not be parsed')

                this.isLoading = false;
                return false
            }

            // Initialize data based on response result
            this.stagesData = result.survey ? result.survey : [];
            this.answersData = result.answer?.data ? result.answer.data : [];
            this.answerEditUrl = result.answer?.answerEditURL ? result.answer.answerEditURL : ''
            this.isDraft = result.answer?.isDraft

            if(this.isDraft){
                this.answerToken = result.answer.answerToken;
                this.answerId =  result.answer.answerId;
            }

            if (this.stagesData) {
                this.addStageNavigationData(this.stagesData.data.stages);
                this.updateStageAccessStatus(this.stagesData.data.stages);
                this.managePointsByPathType();
            }

            this.isLoading = false;
            return true
        },
        /**
         * Add path information to stages
         * Add default access information to stages
         * @param {Array} stagesArray 
         */
        addStageNavigationData(stagesArray) {
            stagesArray.forEach((obj) => {
                // Get array of connectors that form path to current stage
                var pathToCurrentStageArray = [];
                this.getPathToCurrentStageRecursively(obj.id, pathToCurrentStageArray)

                // Gather path stage information 
                const availableStages = pathToCurrentStageArray.reverse().reduce((acc, curVal) => {
                    acc.push(curVal.source)
                    return acc;
                }, [])

                // Include ancestors of each stage for simpler routing checks
                obj.ancestors = availableStages;

                // Add default access info for simpler access checks
                obj.access = {
                    map: false,
                    question: true,
                    questionAccessAllowedByRelations: true
                }

                // Add stage into answerred questions if set to be done by default
                if (obj?.node?.components[0]?.type === "doneByDefault") {
                    this.manageAnswers(obj.id, "doneByDefault")
                }

            })
        },
        hasAllowedRelation(id) {
            let accessAllowed = true;

            const answerData = this.answersData
            const relations = this.stagesData.data.relations

            if(!relations){
                return false;
            }
            
            const matchedRelations = relations.filter(obj => obj.targetId === id);
            
            if(!matchedRelations){
                return false;
            }

            const hasRadioAnswerHelper = (answer, rule) => {
                return answer.answer === rule.title[answer.locale]
            }

            const hasCheckboxAnswerHelper = (answer, rule) => {
                let hasAnswer = false;
                answer.answer.forEach((obj) => {
                    if(obj === rule.title[answer.locale]){
                        hasAnswer = true;
                    }
                })
                return hasAnswer
            }

            const hasAnswerHelper = (relation, answer) => {
                let hasAnswer = false;
                relation.rules.forEach(rule => {

                    // TODO: add support for all question types
                    if (rule.sourceType === 'radio' || rule.sourceType === 'choices') {
                        if (hasRadioAnswerHelper(answer, rule)) {
                            hasAnswer = true;
                        }
                    }
                    if (rule.sourceType === 'checkbox') {
                        if (hasCheckboxAnswerHelper(answer, rule)) {
                            hasAnswer = true;
                        }
                    }
                })

                return hasAnswer;
            }

            matchedRelations.forEach(relation => {
                const matchedAnswers = answerData.filter(obj => obj.id === relation.sourceId)
                if(matchedAnswers.length == 0){
                    if (relation.logicType === "accessibleif") {
                        accessAllowed = false; // Access is never allowed as answer is missing
                    }
                    return;
                }

                matchedAnswers.forEach(answer => {
                    accessAllowed = false; // Set access status to false before access checks
                    const hasAnswer = hasAnswerHelper(relation, answer);

                    // Answer is found = access allowed
                    if (relation.logicType === "accessibleif") {
                        if (hasAnswer) {
                            accessAllowed = true;
                        }
                    }

                    // Answer is not found = access allowed
                    if (relation.logicType === "accessibleifnot") {
                        if (!hasAnswer) {
                            accessAllowed = true;
                        }
                    }
                })
            })

            return accessAllowed;
        },
        /**
         * Update access status of stages based on answers
         * @param {Array} stagesArray
         */
        updateStageAccessStatus(stagesArray) {
            stagesArray.forEach((obj) => {
                const allowMapAccess = this.answersData.some(item => {
                    if (obj.id === item.id) {
                        return true; // Question itself has been answered, allow access
                    } else if (obj?.ancestors.length && obj.ancestors[0] === item.id) {
                        return true; // Direct parent of question has been answered, allow access
                    }
                    return false;
                })

                if(obj?.ancestors?.length === 0){
                    // this is root stage
                    obj.access.map = true;
                } else {
                    obj.access.map = allowMapAccess;
                }

                const allowQuestionAccess = this.hasAllowedRelation(obj.id)

                // If question could be accessed via map, set access info into it and all of its children questions
                if (allowMapAccess) {
                    // If question is blocked by relation, it should be included in answersData array 
                    // as "default answer" with 0 points and cleared if question no longer is blocked 
                    // (-> ancestor answer has been changed, allowing current answer to be answered by used)

                    const relations = this.stagesData.data.relations
                    let matchedRelations;
                    if(relations){
                        matchedRelations = relations.filter(relation => relation.targetId === obj.id);
                    }

                    const ancestorAnswer = this.getAllAnswersByStageId(obj?.ancestors[0]);

                    if(obj?.ancestors?.length > 0 && matchedRelations?.length > 0) {
                        if (allowQuestionAccess) {
                            this.removeAnswer(obj.id)
                            obj.access.question = true;
                            obj.access.questionAccessAllowedByRelations = true;
                        } else {
                            this.manageAnswers(obj.id, "blockedByRelations")
                            obj.access.question = false;
                            obj.access.questionAccessAllowedByRelations = false;
                        }
                    } else if (obj?.ancestors?.length === 0){
                        obj.access.question = true;
                        obj.access.questionAccessAllowedByRelations = true;
                    } else if (ancestorAnswer?.answer === 'blockedByRelations') {
                        this.manageAnswers(obj.id, "blockedByRelations")
                        obj.access.question = false;
                        obj.access.questionAccessAllowedByRelations = false;
                    } else if (ancestorAnswer?.answer !== 'blockedByRelations') {
                        this.removeAnswer(obj.id)
                        obj.access.question = true;
                        obj.access.questionAccessAllowedByRelations = true;
                    }
                }
            })
        },
        setLanguage(lang) {
            this.language = lang;
        },
        setPreviewMode(data) {
            this.previewMode = data;
        },
        setCurrentStage(stageid){
            this.stage = Number(stageid)
        },
        answerExists(stageid){
            return this.answersData.some(item => item.id == Number(stageid) && item.answer.length > 0)
        },
        removeAnswer(stageid){
            const answer = this.getAllAnswersByStageId(stageid)

            if(answer?.answer === "blockedByRelations"){
                const index = this.answersData.indexOf(answer);
                if (index > -1) { // only splice array when item is found
                    this.answersData.splice(index, 1); // 2nd parameter means remove one item only
                }
            }
        },
        manageAnswers(stageid, answer, index){

            if (!stageid && stageid !== 0) {
                return;
            }

            const answerExists = this.answersData.some(item => item.id == stageid)

            if(!answerExists){
                this.answersData.push({
                    "id": Number(stageid),
                    "answer": answer,
                    "index": index,
                    "locale": this.language
                })
            }
            else{
                let updatedItem = this.answersData.find(obj => obj.id == stageid)
                updatedItem.answer = answer
                updatedItem.index = index
                updatedItem.locale = this.language
            }
        },
        getAllAnswersByStageId(stageid){
            return this.answersData.find(obj => obj.id == stageid)
        },
        getPathToCurrentStageRecursively(targetId, connectorsArray) {
            const connector = this.getAllConnectors.find((ctr) => ctr.target === targetId);
            if (connector) {
                connectorsArray.unshift(connector);
                this.getPathToCurrentStageRecursively(connector.source, connectorsArray);
            }
        },
        getAllAnswersByPathTypeId(pathTypeId){
            const answers = []

            this.answersData.map((answer) => {

                if(answer !== null && answer !== undefined) {
                    const ans = this.getAllStages.filter(obj => (obj.node.pathType === pathTypeId && obj.id === answer.id))
                    if(ans.length > 0) {
                        answers.push({
                            "id": Number(answer.id),
                            "answer": answer.answer,
                            "pathType": pathTypeId,
                            "locale": this.language,
                            "options": ans[0].node.components[0].options
                        })
                    }
                }
            })

            return answers
        },
        calculateSurveyProgressInPercents(){
            const stagesInTotal = this.getSurveySize
            const answersGiven = this.getNumberOfGivenAnswers

            return Math.round(answersGiven / stagesInTotal * 100)
        },
        toggleMap(){
            this.showMap = !this.showMap;
        },
        calculateTotalPoints(){
            const totalPoints = this.pointsPerStage.reduce((acc, curr) => acc + (curr.points ? curr.points.reduce((a, b) => a + b, 0) : 0), 0);
            this.totalPoints = totalPoints
        },
        surveyStageExists(stageid){
            return this.stagesData.data.stages.some(item => item.id == stageid)
        },
        getMaxPointsPerPathType(stagesByPathType){
            let maxPoints = 0
            stagesByPathType.forEach((stage) => {
                maxPoints += stage.node.components[0].maxPointsPossible
            })
            return maxPoints;
        },
        managePointsByPathType(){
            this.getPathTypes.map((pathType) => {

                const stageanswers = this.getAllAnswersByPathTypeId(pathType.id)
                const stagePoints = []

                stageanswers.forEach((stage) => {
                    if (stage !== null && stage !== undefined) {
                        stage.options.forEach(option => {
                            const option1 = option.title[this.language]

                            if (stage.answer?.indexOf(option1) !== -1) {
                                stagePoints.push(option.weight)
                            }
                        });
                    }   
                })

                const currentPoints = this.pointsPerPathType;
                const pointsExists = currentPoints.some(item => item.id == pathType.id)

                // If stage points does not exist
                if (!pointsExists) {
                    const stagesByPathType = this.getAllStages.filter(obj => (obj.node.pathType === pathType.id))

                    currentPoints.push({
                        "id": pathType.id,
                        "name": pathType.name[this.language],
                        "stagesTotal": stagesByPathType.length,
                        "stagesAnswered": stageanswers.length,
                        "points": stagePoints.reduce((a, b) => a + b, 0),
                        "maxPoints": this.getMaxPointsPerPathType(stagesByPathType),
                    })
                }

                // Otherwise update
                else {
                    let updatedItem = currentPoints.find(obj => obj.id == pathType.id)
                    updatedItem.points = stagePoints.reduce((a, b) => a + b, 0)
                    updatedItem.stagesAnswered = stageanswers.length
                }
            })
        },
        managePointsByStage(stageid){
            const stageanswers = this.getAllAnswersByStageId(stageid)
            const stagePoints = []
            if(stageanswers != undefined || stageanswers != null){
                this.getStageById(stageid)[0].node.components[0].options.forEach(option => {
                    const option1 = option.title[this.language]

                    if(stageanswers.answer.indexOf(option1) !== -1) {
                        stagePoints.push(option.weight)
                    }
                });
            }

            const currentPoints = this.pointsPerStage;
            const pointsExists = currentPoints.some(item => item.id == stageid)

            // If stage points does not exist
            if(!pointsExists){
                currentPoints.push({
                    "id": stageid,
                    "points": stagePoints
                })
            }

            // Otherwise update
            else{
                let updatedItem = currentPoints.find(obj => obj.id == stageid)
                updatedItem.points = stagePoints
            }
        },
        manageTotalPoints(){
            /* used onMounted */
            /* Manage Total points per stage if form has answers to process */
            const pointsPerStage = []

            this.getAllStages.map((stage) =>{
                const stageAnswers = this.getAllAnswersByStageId(stage.id)

                if(stageAnswers != undefined || stageAnswers != null){
                    this.getStageById(stage.id)[0].node.components[0].options.forEach(option => {
                        const option1 = option.title[this.language]

                        if(stageAnswers.answer.indexOf(option1) !== -1) {
                            pointsPerStage.push(option.weight)
                        }
                    });

                    this.managePointsByStage(stage.id)
                }
            });

            this.totalPoints = pointsPerStage.reduce((a, b) => a + b, 0)
        },
        setSurveyAnswerUuid(uuid){
            this.surveyAnswerUuid = uuid;
        },
        setNameSpace(namespace){
            this.nameSpace = namespace;
        },
        setAnswerURL(url){
            this.answerURL = url;
        }
    },
    getters: {
        getAnswerData: (state) => {
            return state.answersData;
        },
        getGivenAnswers: (state) => {
            return state.answersData.filter(el => {
                return el.answer != '' || el.answer.length != 0;
            })
        },
        getNumberOfGivenAnswers: (state) => {
            return state.getGivenAnswers.length
        },
        getSurveySize: (state) => {
            return state.stagesData.data.stages.length
        },
        getAllStages: (state) => state.stagesData.data.stages,
        getStageById: (state) => {
            return (stageId) => {
                let id = stageId
                let surveyStages = state.stagesData.data.stages;

                return surveyStages.filter(function(stage) {
                    return stage.id == id;
                });
            }
        },
        getPathTypes: (state) => state.stagesData.data.pathTypes,
        getPathById: (state) => {
            return (id) => state.stagesData.data.pathTypes.filter(path => path.id === id)
        },
        getPathDetailsByStageId: (state) => {
            return (stageid) => {
                return state.getPathById(state.getStageById(stageid)[0]?.node.pathType)
            }
        },
        getAllRelations: (state) => state.stagesData.data.relations,
        getAllConnectors: (state) => state.stagesData.data.connectors,
        getConnectorById: (state) => {
            return (id) => {
                const path = state.stagesData.data.connectors.filter(connector => connector.id === id)
                return path
            }
        },
        getConnectorByCoordinates: (state) => {
            return (source,target) => {
                return state.stagesData.data.connectors.filter(connector => connector.source === source && connector.target === target)
            }
        },
        getChoicesDetails: (state) => {
            return (stageId) => {
                const givenAnswer = state.answersData.find(answer => answer.id == stageId)

                if(givenAnswer !== undefined){
                    if(givenAnswer.id !== null && givenAnswer.index !== null){
                        const choiceContent = state.getStageById(givenAnswer.id)[0].node.components[0].options[givenAnswer.index]?.choice?.content[state.language]
                        const choiceType = state.getStageById(givenAnswer.id)[0].node.components[0].options[givenAnswer.index]?.choice?.type

                        return {"content": choiceContent, "type": choiceType};
                    }
                }
            }
        },
        getUnAnsweredStages: (state) => {
            return state.getAllStages.filter(obj1 => !state.getGivenAnswers.some(obj2 => obj1.id === obj2.id))
        },
        getNameSpace: (state) => {
            return state.nameSpace;
        },
        getSurveyAnswerUuid: (state) => {
            return state.surveyAnswerUuid;
        },
        getAnswerURL: (state) => {
            return state.answerURL;
        }
    }
})